import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import type { DocumentLoad, TargetSchema } from "@/types";
import { SectionCard } from "@/components/common/SectionCard";
import { formatRecordType } from "../../utils/documentUtils";
import { ENTITY_CONFIG, GroupedEntityIds, countOperations, formatOperationSummary } from "./config";
import { LoadedRecordsList } from "./LoadedRecordsList";
import type { RowAttention } from "./loadResultModel";
import { FailedRecordsAccordion } from "./FailedRecordsAccordion";

interface SchemaResultCardProps {
  group: GroupedEntityIds;
  /** All failed records on the latest load. Filtered to this schema inside
   *  the card so callers can pass `latestLoad.failed_records` directly. */
  failedRecords: DocumentLoad["failed_records"];
  /** Load warnings by claimant id, rendered on the matching rows. */
  attention?: Record<string, RowAttention>;
}

/**
 * Per-target-schema result card.
 *
 * Wraps the schema's loaded records (`LoadedRecordsList`) and any failed
 * records for the same schema (`FailedRecordsAccordion`). Title and subtitle
 * mirror the legacy `EntitySection`'s "Claimants — 1 record · 1 updated"
 * convention so the operation-summary string stays consistent.
 */
export const SchemaResultCard = ({ group, failedRecords, attention }: SchemaResultCardProps) => {
  // Guard against unknown TargetSchema values (new backend enum, legacy
  // record) — without a fallback, dereferencing `config.labelPlural` would
  // crash the entire Result tab.
  const config = ENTITY_CONFIG[group.targetSchema];
  const schemaLabel = config?.labelPlural ?? formatRecordType(String(group.targetSchema));

  const operationCounts = useMemo(() => countOperations(group.operations), [group.operations]);
  const totalLoaded = group.entityIds.length;
  const schemaFailures = useMemo(
    () => (failedRecords ?? []).filter((r) => r.target_schema === group.targetSchema),
    [failedRecords, group.targetSchema]
  );

  const subtitle = [`${totalLoaded} record${totalLoaded !== 1 ? "s" : ""}`, formatOperationSummary(operationCounts)]
    .filter(Boolean)
    .join(" · ");

  return (
    <SectionCard title={schemaLabel} subtitle={subtitle} noPadding compact>
      <Box sx={{ px: 0.5, pb: schemaFailures.length > 0 ? 1.5 : 0 }}>
        {config ? (
          <LoadedRecordsList {...group} attention={attention} />
        ) : (
          // Schema isn't in ENTITY_CONFIG (likely a new backend enum value
          // not yet wired into the frontend). Show a soft notice instead of
          // attempting to render rows we can't format or navigate from.
          <Box sx={{ px: 1.5, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Records for this schema can't be displayed yet. The schema may need to be added to the local config.
            </Typography>
          </Box>
        )}
        {schemaFailures.length > 0 && (
          <Box sx={{ px: 1.5 }}>
            <FailedRecordsAccordion failedRecords={schemaFailures} schemaLabel={schemaLabel} />
          </Box>
        )}
      </Box>
    </SectionCard>
  );
};

/** Convenience grouping helper — moves the legacy
 *  `DocumentLoadedRecordsTab`'s grouping logic into a reusable shape so the
 *  Result tab can iterate over schemas without inline reduce calls. */
export const groupLoadedRecordsBySchema = (
  loadedRecords: NonNullable<DocumentLoad["loaded_records"]>
): GroupedEntityIds[] => {
  const groups: Partial<Record<TargetSchema, GroupedEntityIds>> = {};
  for (const record of loadedRecords) {
    let group = groups[record.target_schema];
    if (!group) {
      group = { targetSchema: record.target_schema, entityIds: [], operations: {} };
      groups[record.target_schema] = group;
    }
    group.entityIds.push(record.entity_id_ref);
    group.operations[record.entity_id_ref] = record.operation;
  }
  return Object.values(groups) as GroupedEntityIds[];
};
